import type { Diet, SelectableItem } from '@physitrack/championhealth-types';

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { capitalizeFirstChar } from '../../shared/util/assessments';
import { Clock } from '../questions/clock/Clock';
import { NumberSelectQuestion } from '../questions/numberSelectQuestion/NumberSelectQuestion';
import { OneToTenSlider } from '../questions/painSlider/OneToTenSlider';
import { Radio } from '../questions/radio/Radio';
import { AssessmentPageItem } from '../types/AssessmentPageItem';

export function Nutrition1({
  active,
  assessmentValues,
  questionid,
  questionHeading,
  questionText,
  regionContent,
  onOpenModal,
  onSetValue,
}: AssessmentPageItem & { regionContent: any }) {
  const { t } = useTranslation();
  const dietOptions: SelectableItem<Diet>[] = [
    { id: 'meat', title: t('NT001') },
    { id: 'pescatarian', title: t('NT002') },
    { id: 'vegetarian', title: t('NT003') },
    { id: 'vegan', title: t('NT004') },
  ];

  const textValue = [t('NT005'), t('NT006')];

  let whoToContact;
  if (regionContent) {
    whoToContact = [
      { key: 0, value: regionContent.eating_disorder_box_1 },
      { key: 1, value: regionContent.eating_disorder_box_2 },
      { key: 2, value: regionContent.eating_disorder_box_3 },
    ];
  }
  const whoToContactOptions = [];
  whoToContact.map((contact) => whoToContactOptions.push(contact));

  useEffect(() => {
    const currentConditions = assessmentValues.current_mental_health_conditions as
      | { id: string }[]
      | undefined;
    const pastConditions = assessmentValues.past_mental_health_conditions as
      | { id: string }[]
      | undefined;

    if (currentConditions && assessmentValues.mental_health_diagnosis === 'yes' && regionContent) {
      if (
        currentConditions.find((item) => item.id === 'eating') ||
        pastConditions?.find((item) => item.id === 'eating_sq')
      ) {
        let html;
        if (currentConditions && currentConditions.find((item) => item.id === 'eating')) {
          const modalWithBoxesText = {
            main: regionContent.eating_disorder_current_intro,
            secondary: whoToContactOptions,
          };
          html = modalWithBoxesText;
        } else {
          const modalWithBoxesText = {
            main: regionContent.eating_disorder_previous_intro,
            secondary: whoToContactOptions,
          };
          html = modalWithBoxesText;
        }
        if (html) {
          setTimeout(() => {
            onOpenModal(html);
          }, 700);
        }
      }
    }
  }, [assessmentValues.current_mental_health_conditions, assessmentValues.mental_health_diagnosis]);

  useEffect(() => {
    if (assessmentValues.diet !== 'meat') {
      onSetValue({ id: 'red_meat', value: null });
    }
  }, [assessmentValues.diet]);

  return (
    <div className={`question_container ${active}`} id={questionid}>
      <h1>{questionHeading}</h1>
      <p>{questionText}</p>

      <Radio
        questionid="diet"
        width="_wide"
        questionTitle={t('assessment.question.diet')}
        options={dietOptions}
        onSetValue={onSetValue}
        assessmentValues={assessmentValues}
        modalText={t('NT008')}
        onOpenModal={onOpenModal}
      />

      {assessmentValues.diet === 'meat' && (
        <NumberSelectQuestion
          questionid="red_meat"
          questionTitle={t('assessment.question.red_meat')}
          minlimit={0}
          maxlimit={10}
          width=""
          onSetValue={onSetValue}
          assessmentValues={assessmentValues}
          fadeIn="yes"
          modalText={t('NT011')}
          onOpenModal={onOpenModal}
          skippable
          controlsInfo={t('A138')}
          labelText={capitalizeFirstChar(t('NT010'))}
        />
      )}

      <NumberSelectQuestion
        questionid="vegetables"
        questionTitle={t('assessment.question.vegetables')}
        minlimit={0}
        maxlimit={10}
        width=""
        onSetValue={onSetValue}
        assessmentValues={assessmentValues}
        modalText={t('NT014')}
        onOpenModal={onOpenModal}
        skippable
        controlsInfo={t('A138')}
        labelText={capitalizeFirstChar(t('NT013'))}
      />

      <NumberSelectQuestion
        questionid="takeaways"
        questionTitle={t('assessment.question.takeaways')}
        minlimit={0}
        maxlimit={20}
        width=""
        onSetValue={onSetValue}
        assessmentValues={assessmentValues}
        skippable
        controlsInfo={t('A139')}
        labelText={capitalizeFirstChar(t('A139'))}
      />
      <hr />
      <fieldset data-input-type="meal-time">
        <legend className="assessment_question_title">{t('NT016')}</legend>
        <hr />
        <div className="meal_time_container_inner">
          <Clock
            questionid="first_meal"
            width=""
            questionTitle={t('assessment.question.first_meal')}
            startHour={7}
            startMinute={0}
            onSetValue={onSetValue}
            assessmentValues={assessmentValues}
          />
          <Clock
            questionid="last_meal"
            width=""
            questionTitle={t('assessment.question.last_meal')}
            startHour={18}
            startMinute={30}
            onSetValue={onSetValue}
            assessmentValues={assessmentValues}
          />
        </div>
      </fieldset>

      <OneToTenSlider
        questionid="nutrition_quality"
        questionTitle={t('assessment.question.nutrition_quality')}
        width="_wide"
        textValues={textValue}
        onSetValue={onSetValue}
        assessmentValues={assessmentValues}
        modalText={t('NT020')}
        onOpenModal={onOpenModal}
      />
    </div>
  );
}
